import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { useAuthzRollout } from './AuthzRolloutProvider'
import { isOnPermissionsService, recordAuthorizationQueryOptions } from './recordAuthorizationQuery'
import type { AskedRecord } from './askedRecord'
import type { ResolvedRecordAuthorization } from './recordAuthorizationFetch'
import type { RecordPermissionsEntity } from '@omnea-digital/authz-model'

export type RecordAuthorization<Entity extends RecordPermissionsEntity> =
  | { entity: Entity; status: 'loading' }
  // The check failed, so nothing is permitted.
  | { entity: Entity; status: 'unavailable'; error: unknown }
  // The organisation is not on the permissions service, so the role rules decide.
  | { entity: Entity; status: 'role' }
  | ResolvedRecordAuthorization<Entity>

export type AnyRecordAuthorization = RecordAuthorization<RecordPermissionsEntity>

/** What the caller may do to one record, or null where there is no record to ask about. */
export function useRecordAuthorization<Entity extends RecordPermissionsEntity>(
  asked: AskedRecord<Entity>
): RecordAuthorization<Entity>
export function useRecordAuthorization<Entity extends RecordPermissionsEntity>(
  asked: AskedRecord<Entity> | null
): RecordAuthorization<Entity> | null
export function useRecordAuthorization<Entity extends RecordPermissionsEntity>(
  asked: AskedRecord<Entity> | null
): RecordAuthorization<Entity> | null {
  const { getToken, user } = useAuthenticatedUser()
  const rollout = useAuthzRollout()
  const entity = asked?.entity ?? null

  const { data, isError, error } = useQuery({
    ...recordAuthorizationQueryOptions(getToken, user.id, asked),
    enabled: isOnPermissionsService(rollout),
    refetchOnWindowFocus: true,
  })

  return useMemo((): RecordAuthorization<Entity> | null => {
    if (entity == null) return null
    // A rollout that has not answered is not an organisation off the service: treating it as one shows every
    // affordance, then withdraws some when the answer lands.
    if (rollout.status === 'pending') return { entity, status: 'loading' }
    if (!isOnPermissionsService(rollout)) return { entity, status: 'role' }
    // Read before isError so a failed background refetch keeps the last-known answer rather than
    // collapsing every affordance mid-session.
    if (data != null) return data
    if (isError) return { entity, status: 'unavailable', error }
    return { entity, status: 'loading' }
  }, [entity, rollout, data, isError, error])
}
